import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Plus, Pencil, Trash2, Upload, Database, FileText, Globe } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  useConfiguracionCEMC,
  useFuentesCEMC,
  useCursosCEMC,
  useDeleteFuenteCEMC,
  useDeleteCursoCEMC,
  useCreateCursoCEMC,
  useUpdateCursoCEMC,
} from '../hooks/useCEMC'
import { FuenteFormSheet } from '../components/FuenteFormSheet'
import { cemcApi } from '../api/cemc.api'
import type { FuenteDatosCEMC, CursoCEMC } from '../types'

export function GestionFuentesPage() {
  const { configId } = useParams<{ configId: string }>()
  const navigate = useNavigate()
  const id = Number(configId)

  const { data: config, isLoading: configLoading } = useConfiguracionCEMC(id)
  const { data: fuentes = [], isLoading: fuentesLoading } = useFuentesCEMC(id)
  const { data: cursos = [], isLoading: cursosLoading } = useCursosCEMC(id)

  const [fuenteFormOpen, setFuenteFormOpen] = useState(false)
  const [editingFuente, setEditingFuente] = useState<FuenteDatosCEMC | null>(null)
  const [cursoForm, setCursoForm] = useState<Partial<CursoCEMC> | null>(null)

  const deleteFuente = useDeleteFuenteCEMC()
  const deleteCurso = useDeleteCursoCEMC()
  const createCurso = useCreateCursoCEMC()
  const updateCurso = useUpdateCursoCEMC()

  const handleUploadArchivo = async (fuente: FuenteDatosCEMC) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.csv,.xlsx,.xls'
    input.onchange = async () => {
      if (!input.files?.[0]) return
      try {
        await cemcApi.uploadArchivo(fuente.id, input.files[0])
        window.location.reload()
      } catch {}
    }
    input.click()
  }

  const handleSaveCurso = async () => {
    if (!cursoForm) return
    try {
      if (cursoForm.id) {
        await updateCurso.mutateAsync({ id: cursoForm.id, payload: cursoForm })
      } else {
        await createCurso.mutateAsync({ configId: id, payload: cursoForm })
      }
      setCursoForm(null)
    } catch {}
  }

  if (configLoading) return <div className="p-8"><Skeleton className="h-8 w-64" /></div>

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/cemc-educacion/configuracion')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">{config?.nombre}</h1>
          <p className="text-sm text-muted-foreground">{config?.grupo_nombre}</p>
        </div>
      </div>

      <Tabs defaultValue="fuentes">
        <TabsList>
          <TabsTrigger value="fuentes">Fuentes de Datos</TabsTrigger>
          <TabsTrigger value="cursos">Cursos</TabsTrigger>
        </TabsList>

        <TabsContent value="fuentes" className="space-y-3 mt-4">
          <div className="flex justify-end">
            <Button
              onClick={() => { setEditingFuente(null); setFuenteFormOpen(true) }}
              className="bg-violet-700 hover:bg-violet-800 text-white"
              size="sm"
            >
              <Plus className="mr-2 h-4 w-4" />
              Nueva fuente
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Tabla / Archivo</TableHead>
                    <TableHead className="text-center">Orden</TableHead>
                    <TableHead className="text-center">Activo</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fuentesLoading
                    ? [1,2].map((i) => <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-4" /></TableCell></TableRow>)
                    : fuentes.map((fuente) => (
                      <TableRow key={fuente.id}>
                        <TableCell className="font-medium">{fuente.nombre}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="gap-1">
                            {fuente.tipo === 'bd'
                              ? <><Database className="h-3 w-3" /> BD</>
                              : fuente.tipo === 'archivo'
                              ? <><FileText className="h-3 w-3" /> Archivo</>
                              : <><Globe className="h-3 w-3" /> Google</>}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">{fuente.rol || '—'}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {fuente.tipo === 'bd'
                            ? fuente.tabla_nombre || '—'
                            : fuente.tipo === 'archivo'
                            ? fuente.archivo_nombre_original || '—'
                            : fuente.google_sheet_url
                            ? fuente.google_sheet_url.slice(0, 45) + '…'
                            : '—'}
                        </TableCell>
                        <TableCell className="text-center">{fuente.orden}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant={fuente.activo ? 'default' : 'secondary'}>
                            {fuente.activo ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {fuente.tipo === 'archivo' && (
                              <Button size="sm" variant="ghost" title="Subir archivo" onClick={() => handleUploadArchivo(fuente)}>
                                <Upload className="h-4 w-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => { setEditingFuente(fuente); setFuenteFormOpen(true) }}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              className="text-destructive hover:text-destructive"
                              onClick={() => window.confirm('¿Eliminar fuente?') && deleteFuente.mutate(fuente.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cursos" className="space-y-3 mt-4">
          <div className="flex justify-end">
            <Button
              onClick={() => setCursoForm({ configuracion_id: id, num_modulos: 1, orden: 0, activo: true, descripcion: '' })}
              className="bg-violet-700 hover:bg-violet-800 text-white"
              size="sm"
            >
              <Plus className="mr-2 h-4 w-4" />
              Nuevo curso
            </Button>
          </div>

          {cursoForm && (
            <Card className="border-violet-200">
              <CardHeader><CardTitle className="text-sm">{cursoForm.id ? 'Editar curso' : 'Nuevo curso'}</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <div>
                    <label className="text-xs font-medium">Nombre</label>
                    <input
                      className="w-full border rounded px-2 py-1 text-sm mt-1"
                      value={cursoForm.nombre || ''}
                      onChange={(e) => setCursoForm({ ...cursoForm, nombre: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Código (slug)</label>
                    <input
                      className="w-full border rounded px-2 py-1 text-sm mt-1"
                      value={cursoForm.codigo || ''}
                      onChange={(e) => setCursoForm({ ...cursoForm, codigo: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Núm. Módulos</label>
                    <input
                      type="number"
                      className="w-full border rounded px-2 py-1 text-sm mt-1"
                      value={cursoForm.num_modulos || 1}
                      onChange={(e) => setCursoForm({ ...cursoForm, num_modulos: Number(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium">Fuente</label>
                    <select
                      className="w-full border rounded px-2 py-1 text-sm mt-1"
                      value={cursoForm.fuente_id || ''}
                      onChange={(e) => setCursoForm({ ...cursoForm, fuente_id: e.target.value ? Number(e.target.value) : null })}
                    >
                      <option value="">Sin fuente</option>
                      {fuentes.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
                    </select>
                  </div>
                </div>
                {(cursoForm.num_modulos || 0) > 0 && (
                  <div className="mt-2">
                    <label className="text-xs font-medium text-muted-foreground">Puntaje mínimo de aprobación por módulo (default: 1)</label>
                    <div className="flex gap-2 mt-1 flex-wrap">
                      {Array.from({ length: cursoForm.num_modulos || 0 }, (_, i) => i + 1).map((n) => (
                        <div key={n} className="flex flex-col items-center gap-0.5">
                          <span className="text-xs text-muted-foreground">M{n}</span>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            className="w-14 border rounded px-1 py-0.5 text-xs text-center"
                            value={(cursoForm.umbral_puntaje as Record<string, number>)?.[n] ?? 1}
                            onChange={(e) => {
                              const prev = (cursoForm.umbral_puntaje as Record<string, number>) || {}
                              setCursoForm({ ...cursoForm, umbral_puntaje: { ...prev, [n]: Number(e.target.value) } })
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={handleSaveCurso} className="bg-violet-700 hover:bg-violet-800 text-white">
                    Guardar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setCursoForm(null)}>Cancelar</Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead className="text-center">Módulos</TableHead>
                    <TableHead>Fuente</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cursosLoading
                    ? [1,2].map((i) => <TableRow key={i}><TableCell colSpan={5}><Skeleton className="h-4" /></TableCell></TableRow>)
                    : cursos.map((curso) => (
                      <TableRow key={curso.id}>
                        <TableCell className="font-medium">{curso.nombre}</TableCell>
                        <TableCell><Badge variant="outline">{curso.codigo}</Badge></TableCell>
                        <TableCell className="text-center">{curso.num_modulos}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {fuentes.find((f) => f.id === curso.fuente_id)?.nombre || '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => setCursoForm(curso)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              className="text-destructive hover:text-destructive"
                              onClick={() => window.confirm('¿Eliminar curso?') && deleteCurso.mutate(curso.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <FuenteFormSheet
        open={fuenteFormOpen}
        onClose={() => setFuenteFormOpen(false)}
        configId={id}
        editing={editingFuente}
      />
    </div>
  )
}
